/**
 * client/ui/quests.ts
 * Responsabilidad: Controlador del Modal de Misiones Diarias, Logros y Canalización Astral.
 *
 * Jerarquía DOM: Se inserta como hijo directo del <body>.
 * Estilo: Glassmorphism oscuro con acentos alquímicos cian, dorado y púrpura.
 */

import Swal from "sweetalert2";
import { questManager } from "../quests";
import type { QuestItem } from "../types";
import { t } from "../i18n";
import { SeasonPrizesUI } from "./season-prizes";

export interface QuestsUIOptions {
    onOpen?: () => void;
    onClose?: () => void;
}

export class QuestsManagerUI {
    private activeTab: "daily" | "achievements" | "prizes" = "daily";
    private options: QuestsUIOptions;
    private timerInterval: ReturnType<typeof setInterval> | null = null;

    // Elementos del DOM
    private modal = document.getElementById("quests-modal");
    private closeBtn = document.getElementById("quests-close");
    private hudBtn = document.getElementById("quests-btn");
    private menuBtn = document.getElementById("open-quests-btn") || document.getElementById("alias-quests-btn");
    private hudBadge = document.getElementById("quests-badge");
    private menuBadge = document.getElementById("alias-quests-badge");

    private crystalsDisplay = document.getElementById("user-crystals-display");
    private orbsDisplay = document.getElementById("user-orbs-display");
    private resetTimerDisplay = document.getElementById("quests-reset-timer");
    private resetLabelDisplay = document.querySelector(".quests-reset-wrap .reset-label") as HTMLElement | null;

    private tabDailyBtn = document.getElementById("tab-daily-quests");
    private tabAchievementsBtn = document.getElementById("tab-achievements");
    private tabPrizesBtn = document.getElementById("tab-season-prizes");
    private questsListContainer = document.getElementById("quests-list-container");

    private watchAdBtn = document.getElementById("quests-watch-ad-btn") as HTMLButtonElement | null;
    private adStatusText = document.getElementById("quests-ad-status");

    constructor(options: QuestsUIOptions = {}) {
        this.options = options;
        this.initEventListeners();

        // Suscribirse a cambios del QuestManager para actualizar badges y UI en tiempo real
        questManager.subscribe(() => {
            this.updateBadges();
            if (this.isOpen()) {
                this.renderContent();
            }
        });

        // Actualizar badges iniciales
        this.updateBadges();

        // Timer de countdown visual
        this.timerInterval = setInterval(() => {
            if (this.isOpen()) {
                this.updateResetTimer();
            }
        }, 1000);
    }

    private initEventListeners(): void {
        if (this.hudBtn) {
            this.hudBtn.addEventListener("click", () => this.open());
        }
        if (this.menuBtn) {
            this.menuBtn.addEventListener("click", () => this.open());
        }
        if (this.closeBtn) {
            this.closeBtn.addEventListener("click", () => this.close());
        }

        if (this.tabDailyBtn) {
            this.tabDailyBtn.addEventListener("click", () => {
                this.activeTab = "daily";
                this.updateTabsUI();
                this.renderContent();
                this.updateResetTimer();
            });
        }

        if (this.tabAchievementsBtn) {
            this.tabAchievementsBtn.addEventListener("click", () => {
                this.activeTab = "achievements";
                this.updateTabsUI();
                this.renderContent();
                this.updateResetTimer();
            });
        }

        if (this.tabPrizesBtn) {
            this.tabPrizesBtn.addEventListener("click", () => {
                this.activeTab = "prizes";
                this.updateTabsUI();
                this.renderContent();
                this.updateResetTimer();
            });
        }

        if (this.watchAdBtn) {
            this.watchAdBtn.addEventListener("click", async () => {
                await this.handleWatchAd();
            });
        }

        window.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.isOpen()) {
                this.close();
            }
        });
    }

    public isOpen(): boolean {
        return Boolean(this.modal && !this.modal.classList.contains("hidden"));
    }

    public open(): void {
        if (!this.modal) return;
        this.modal.classList.remove("hidden");
        this.updateTabsUI();
        this.renderContent();
        this.updateResetTimer();

        if (this.options.onOpen) {
            this.options.onOpen();
        }
    }

    public close(): void {
        if (!this.modal) return;
        this.modal.classList.add("hidden");

        if (this.options.onClose) {
            this.options.onClose();
        }
    }

    public toggle(): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

        private updateTabsUI(): void {
        if (this.tabDailyBtn) {
            this.tabDailyBtn.classList.toggle("active", this.activeTab === "daily");
        }
        if (this.tabAchievementsBtn) {
            this.tabAchievementsBtn.classList.toggle("active", this.activeTab === "achievements");
        }
        if (this.tabPrizesBtn) {
            this.tabPrizesBtn.classList.toggle("active", this.activeTab === "prizes");
        }

        // En la pestaña de Premios y Títulos ocultamos Canalización Astral para que la armería y la tabla respiren al 100%
        const astralSec = document.querySelector(".astral-channeling-section") as HTMLElement | null;
        if (astralSec) {
            astralSec.style.display = this.activeTab === "prizes" ? "none" : "";
        }
    }

    private updateBadges(): void {
        const count = questManager.getClaimableCount();
        const text = count > 0 ? (count > 9 ? "9+" : String(count)) : "";

        if (this.hudBadge) {
            this.hudBadge.textContent = text;
            this.hudBadge.classList.toggle("hidden", count === 0);
        }
        if (this.menuBadge) {
            this.menuBadge.textContent = text;
            this.menuBadge.classList.toggle("hidden", count === 0);
        }
    }

    private updateResetTimer(): void {
        if (!this.resetTimerDisplay) return;
        const state = questManager.getState();

        if (this.activeTab === "prizes") {
            const now = new Date();
            const year = now.getUTCFullYear();
            const month = now.getUTCMonth();
            const endOfMonthUtc = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
            const ms = Math.max(0, endOfMonthUtc.getTime() - now.getTime());
            const totalSeconds = Math.max(0, Math.floor(ms / 1000));
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const timeStr = `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

            if (this.resetLabelDisplay) {
                this.resetLabelDisplay.textContent = "⏳ Cierre de temporada en:";
                this.resetTimerDisplay.textContent = timeStr;
            } else {
                this.resetTimerDisplay.textContent = `Cierre de temporada en: ${timeStr}`;
            }
            return;
        }

        if (this.activeTab === "daily") {
            const ms = state.msUntilReset;
            const totalSeconds = Math.max(0, Math.floor(ms / 1000));
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const timeStr = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

            if (this.resetLabelDisplay) {
                this.resetLabelDisplay.textContent = "⏳ Próximo reinicio diario en:";
                this.resetTimerDisplay.textContent = timeStr;
            } else {
                this.resetTimerDisplay.textContent = `Próximo reinicio diario en: ${timeStr}`;
            }
        } else {
            const ms = state.msUntilWeeklyReset ?? 0;
            const totalSeconds = Math.max(0, Math.floor(ms / 1000));
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const timeStr = `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

            if (this.resetLabelDisplay) {
                this.resetLabelDisplay.textContent = "⏳ Reinicio semanal en:";
                this.resetTimerDisplay.textContent = timeStr;
            } else {
                this.resetTimerDisplay.textContent = `Reinicio semanal en: ${timeStr}`;
            }
        }
    }

    private renderContent(): void {
        const state = questManager.getState();

        // 1. Balances destacados
        if (this.crystalsDisplay) {
            this.crystalsDisplay.textContent = `${state.crystals.toLocaleString()} 💎`;
        }
        if (this.orbsDisplay) {
            this.orbsDisplay.textContent = `${state.astralOrbs.toLocaleString()} 🔮`;
        }

        // 2. Sección Canalización Astral (Anuncios)
        if (this.watchAdBtn && this.adStatusText) {
            this.adStatusText.textContent = "Próximamente disponible";
            this.adStatusText.style.color = "#94a3b8";
            this.watchAdBtn.disabled = true;
            this.watchAdBtn.innerHTML = "🎬 Próximamente (+2 🔮, +50 💎)";
        }

        // 3. Lista de Misiones o Logros o Vista de Premios de Temporada
        if (!this.questsListContainer) return;

        if (this.activeTab === "prizes") {
            void SeasonPrizesUI.render(this.questsListContainer, (crystals, orbs) => {
                if (this.crystalsDisplay) {
                    this.crystalsDisplay.textContent = `${crystals.toLocaleString()} 💎`;
                }
                if (this.orbsDisplay) {
                    this.orbsDisplay.textContent = `${orbs.toLocaleString()} 🔮`;
                }
            });
            return;
        }

        this.questsListContainer.innerHTML = "";

        const items: QuestItem[] = this.activeTab === "daily" ? state.dailyQuests : state.achievements;

        if (items.length === 0) {
            const emptyEl = document.createElement("div");
            emptyEl.className = "quests-empty-state";
            emptyEl.textContent = t("misc.loading", { defaultValue: "Cargando..." });
            this.questsListContainer.appendChild(emptyEl);
            return;
        }

        for (const item of items) {
            const card = this.createQuestCard(item);
            this.questsListContainer.appendChild(card);
        }
    }

    private createQuestCard(item: QuestItem): HTMLElement {
        const card = document.createElement("div");
        card.className = `quest-card ${item.claimed ? "quest-claimed" : item.progress >= item.target ? "quest-ready" : "quest-in-progress"}`;

        // Título y descripción autoritativos desde la base de datos:
        const titleText = (item.title && item.title.trim().length > 0)
            ? item.title
            : t(item.titleKey, { defaultValue: item.title });

        const descKey = `quests.desc_${item.id}`;
        const descText = (item.description && item.description.trim().length > 0)
            ? item.description
            : t(descKey, { defaultValue: item.description });

        const pct = Math.min(100, Math.floor((item.progress / item.target) * 100));

        let formattedProgress = `${item.progress.toLocaleString()} / ${item.target.toLocaleString()}`;
        if (item.unit === "ms") {
            const curSec = Math.floor(item.progress / 1000);
            const tarSec = Math.floor(item.target / 1000);
            formattedProgress = `${curSec}s / ${tarSec}s`;
        }

        // Badges de recompensa
        let rewardsHtml = "";
        if (item.rewardAstralOrbs > 0) {
            rewardsHtml += `<span class="reward-pill orbs-pill">+${item.rewardAstralOrbs} 🔮</span>`;
        }
        if (item.rewardCrystals > 0) {
            rewardsHtml += `<span class="reward-pill crystals-pill">+${item.rewardCrystals} 💎</span>`;
        }

        // Botón de acción
        let actionBtnHtml = "";
        if (item.claimed) {
            actionBtnHtml = `<button class="quest-action-btn claimed" disabled>✅ ${t("quests.claimed", { defaultValue: "Reclamado" })}</button>`;
        } else if (item.progress >= item.target) {
            actionBtnHtml = `<button class="quest-action-btn claimable" data-quest-id="${item.id}">🎁 ${t("quests.claim", { defaultValue: "Reclamar" })}</button>`;
        } else {
            actionBtnHtml = `<button class="quest-action-btn disabled" disabled>${pct}%</button>`;
        }

        card.innerHTML = `
            <div class="quest-info">
                <div class="quest-header-row">
                    <h3 class="quest-title">${titleText}</h3>
                    <div class="quest-rewards-wrap">${rewardsHtml}</div>
                </div>
                <p class="quest-desc">${descText}</p>
                <div class="quest-progress-wrap">
                    <div class="quest-progress-bar">
                        <div class="quest-progress-fill" style="width: ${pct}%"></div>
                    </div>
                    <span class="quest-progress-numbers">${formattedProgress}</span>
                </div>
            </div>
            <div class="quest-action-col">
                ${actionBtnHtml}
            </div>
        `;

        // Enganchar click de reclamar
        const claimBtn = card.querySelector<HTMLButtonElement>(".quest-action-btn.claimable");
        if (claimBtn) {
            claimBtn.addEventListener("click", async () => {
                claimBtn.disabled = true;
                claimBtn.textContent = "⏳ ...";
                const success = await questManager.claimReward(item.id);
                if (success) {
                    Swal.fire({
                        title: t("quests.rewardClaimed", { defaultValue: "¡Recompensa reclamada!" }),
                        html: `<p style="font-size: 1.15rem; color: #fbbf24;">${rewardsHtml}</p>`,
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false,
                        background: "rgba(16, 24, 39, 0.98)",
                        color: "#f8fafc",
                    });
                }
            });
        }

        return card;
    }

    private async handleWatchAd(): Promise<void> {
        // Sprint 31: Modo Próximamente activo
        const adsDisabled = true as boolean;
        if (adsDisabled || !this.watchAdBtn) return;
        this.watchAdBtn.disabled = true;
        const originalText = this.watchAdBtn.innerHTML;
        this.watchAdBtn.innerHTML = `<span>⏳ ${t("misc.loading", { defaultValue: "Cargando..." })}</span>`;

        try {
            const res = await questManager.watchAd();
            if (res.success) {
                Swal.fire({
                    title: t("quests.astralChanneling", { defaultValue: "Canalización Astral" }),
                    text: t("quests.adSuccess", { defaultValue: "¡Canalización exitosa! Has recibido 2 🔮 y 50 💎" }),
                    icon: "success",
                    timer: 2500,
                    showConfirmButton: false,
                    background: "rgba(16, 24, 39, 0.98)",
                    color: "#f8fafc",
                });
            } else if (res.error === "daily_limit_reached") {
                Swal.fire({
                    title: t("quests.noAdsLeft", { defaultValue: "Límite diario alcanzado" }),
                    text: t("quests.adsRemaining", { remaining: 0, defaultValue: "Has utilizado las 3 canalizaciones de hoy. Vuelve mañana tras el reseteo UTC." }),
                    icon: "info",
                    background: "rgba(16, 24, 39, 0.98)",
                    color: "#f8fafc",
                });
            }
        } finally {
            if (this.watchAdBtn) {
                this.watchAdBtn.innerHTML = originalText;
                this.watchAdBtn.disabled = questManager.getState().adWatchesRemaining <= 0;
            }
        }
    }
}
